// PRD-1571: Ask ResAI shared queries — the pieces of the sharing model the UI reads.
// Field names follow the PRD-1567 API contract (thread items gain `visibility`,
// `user_id` (owner), `locked`, `pinned_dataset_ids`, `shared_at`).

// Every shared-query control is gated on the profile's
// features.shared_queries_enabled (PRD-1579), off until the profile loads.

export const VISIBILITY_PRIVATE = 'private';
export const VISIBILITY_ORGANIZATION = 'organization';

export const isThreadShared = (thread) => thread?.visibility === VISIBILITY_ORGANIZATION;

// Threads from today's API carry no owner; everything in the list is then the
// caller's own, so a missing `user_id` counts as owned.
export const isThreadOwner = (thread, user) =>
  !thread?.user_id || (user?.id != null && thread.user_id === user.id);

// R4/R5 impact: people other than the owner who have asked at least one turn.
export const countOtherAskers = (turns, ownerId) =>
  new Set((turns || []).map((t) => t.author_id).filter((id) => id && id !== ownerId)).size;

// `others` is null when the count couldn't be fetched: say people may lose
// access rather than block the action or claim nobody is affected.
const othersLine = (others) => {
  if (others == null) return 'Other people in your organization may have asked in this query.';
  return `${others} other ${others === 1 ? 'person has' : 'people have'} asked in this query.`;
};

export const stopSharingMessage = (others) => (others === 0
  ? 'Nobody else has asked in this query yet. It leaves your organization\'s Shared with me list.'
  : `${othersLine(others)} They will lose access to it and it leaves their Shared with me list.\n\n`
    + 'You keep the full conversation, including their questions.');

// A shared query can also be a report automation's conversation; keep that
// warning (see handleDeleteThread) rather than dropping it for this one.
export const deleteSharedMessage = (others, reportName) => (others === 0
  ? 'Nobody else has asked in this query yet. Deleting removes it for everyone.'
  : `${othersLine(others)} Deleting removes it for everyone, including their questions.`)
  + (reportName
    ? `\n\nIt's also linked to the scheduled report "${reportName}". Deleting it won't stop the `
      + 'automation; its next delivery will start a new conversation.'
    : '');

// Ask ResAI Iteration 1 (Shared Queries handoff): who else has asked, as a clause.
// Empty when nobody else has, or when the count couldn't be read.
export const othersWhoAskedClause = (others) => (others > 0
  ? `the ${others === 1 ? 'person who has' : `${others} people who have`} asked in it`
  : '');

// Nobody else asked: the handoff's plain copy. The count couldn't be read: still say it goes
// for everyone, rather than imply nobody else is affected.
export const deleteSharedQueryMessage = (others) => {
  if (others == null) return 'This query and its answers will be deleted for everyone. You can’t undo this.';
  return others > 0
    ? `This query and its answers will be deleted for everyone, including ${othersWhoAskedClause(others)}. You can’t undo this.`
    : 'This query and its answers will be deleted. You can’t undo this.';
};

export const SHARE_ERROR = 'Your query wasn’t shared. Try again.';

// Dialog title around a query name: `Stop sharing "Name"?`, without a second
// question mark when the name already ends in one.
export const confirmTitle = (action, thread) => {
  const name = thread?.title || 'Untitled conversation';
  return `${action} "${name}"${name.endsWith('?') ? '' : '?'}`;
};

// The owner's name comes from the list row (PRD-1569): `owner_name` on every item.
// A backend without it gets generic copy rather than an id.
export const ownerLabel = (thread) => `Owner: ${thread?.owner_name || 'a colleague'}`;

// A locked row carries no title or dataset names for anyone but its owner (PRD-1569 R11,
// micro-services-stack #605), so the reason never names them.
export const lockedReason = (thread) => {
  const title = thread?.title || 'Untitled conversation';
  return `"${title}" uses a dataset you don't have access to. The conversation stays hidden until you do.`;
};

// ── Shared conversation (R7–R9, Frames 2 and 2b) ─────────────────────────────

// People who have asked at least one turn, in the order they first asked (R7).
export const askersFrom = (turns) => {
  const seen = new Map();
  (turns || []).forEach((t) => {
    if (t.author_id && !seen.has(t.author_id)) {
      seen.set(t.author_id, { id: t.author_id, name: t.author_name || '', color: t.author_avatar_color || '' });
    }
  });
  return [...seen.values()];
};

// ── Share events (PRD-1643): where a query was shared, stopped, or shared again ──

// `share_events` on GET /threads/{id}, oldest first; each names no one.
export const SHARE_EVENT_TEXT = {
  shared: 'Shared with your organization',
  stopped: 'Sharing stopped',
  shared_again: 'Shared again',
};

/**
 * The thread's items with each share event placed by time: before the first question asked
 * after it, or at the end. A question with no known time doesn't move an event past it.
 * `timeOf(msg)` gives a question's time in ms, or NaN when it can't be told.
 */
export const withShareEvents = (items, events, timeOf) => {
  const pending = [...(events || [])]
    .filter((e) => SHARE_EVENT_TEXT[e.event] && !Number.isNaN(Date.parse(e.at)))
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  const out = [];
  items.forEach((item) => {
    const at = item.msg?.type === 'human' ? timeOf(item.msg) : NaN;
    while (!Number.isNaN(at) && pending.length && Date.parse(pending[0].at) <= at) out.push({ shareEvent: pending.shift() });
    out.push(item);
  });
  pending.forEach((e) => out.push({ shareEvent: e }));
  return out;
};

export const askedCountLabel = (n) => `${n} ${n === 1 ? 'person has' : 'people have'} asked`;

export const answeredForLabel = (name) => `Answered for ${name} at their access`;

// `/state` marks a human message whose answer this viewer can't see (R8, Frame 2b).
export const isWithheld = (msg) => msg?.additional_kwargs?.withheld === true;

export const WITHHELD_ANSWER = 'This answer was given with access you don\'t have, so it is hidden.';
export const SHARED_COMPOSER_PLACEHOLDER = 'Ask a follow-up. Everyone this query is shared with will see your question and the answer.';
export const SHARED_AUDIENCE = 'Visible to your organization';
// The docked composer's placeholder, shared or not (Shared Queries handoff 2, Composer).
export const COMPOSER_PLACEHOLDER = 'Write a question...';
export const READ_ONLY_ROLE = 'Your role can read this query but not ask in it.';

// ── Live turns (R13) ─────────────────────────────────────────────────────────

// How often an open shared conversation asks who has asked since (PRD p95 < 5 s).
export const TURNS_POLL_MS = 5000;

// The newest turn's position; it grows when anyone asks.
export const maxTurnSeq = (turns) => (turns || []).reduce((max, t) => Math.max(max, t.seq || 0), 0);

// ── Access ending while open (R4) and dataset changes (R10) ─────────────────

// Ask ResAI Iteration 1: why an open shared query can't be read any more. A 404 is
// deleted; a 403 with the code `stopped_sharing` (PRD-1646: detail `{ code, message }`) is
// unshared; a bare 403 means a dataset in it isn't yours to read. resapi sends a bare 403 as
// `{"detail": "Forbidden"}` (Starlette's default), so that counts as bare too. Any other 403 is
// a refusal of something else (a read-only role, a dataset you picked), not the query going.
export const STOPPED_SHARING_CODE = 'stopped_sharing';

export const lostAccessReason = (err) => {
  if (err?.status === 404) return 'deleted';
  if (err?.status !== 403) return null;
  const detail = err.responseData?.detail;
  if (err.code === STOPPED_SHARING_CODE || detail?.code === STOPPED_SHARING_CODE) return 'unshared';
  if (detail == null || detail === '' || detail === 'Forbidden') return 'locked';
  return null;
};

export const QUERY_GONE = {
  unshared: 'This query is no longer shared. You can\u2019t open it or ask in it.',
  deleted: 'This query is no longer available. Its owner deleted it.',
  locked: 'You no longer have access to the data in this query. Contact your administrator for access.',
};

// What's announced when access is lost while the query is open (Shared Queries handoff 2).
export const LOST_ACCESS_ANNOUNCEMENT = 'You no longer have access to the data in this query';

// What a participant sees when the query they have open stops being theirs to
// read. The server answers 404 both when the owner stopped sharing it and when
// they deleted it (the shared-read check runs before anything else), so the
// copy covers both rather than guessing.
export const SHARING_ENDED = {
  title: 'This query is no longer shared with you',
  text: 'The owner stopped sharing it or deleted it. It has left your Shared with me list.',
};

const sameIds = (a = [], b = []) => a.length === b.length && a.every((id) => b.includes(id));

// The turn before `turn` changed datasets when its `datasets` differ. Turns
// backfilled at share time without a recoverable scope come back with
// `datasets: []`; that is unknown, not a change, so it never draws the line.
export const datasetsChangedBefore = (turn, turns) => {
  if (!turn) return false;
  const prev = (turns || []).filter((t) => (t.seq || 0) < (turn.seq || 0))
    .reduce((best, t) => (!best || t.seq > best.seq ? t : best), null);
  if (!prev || !prev.datasets?.length) return false;
  return !sameIds(prev.datasets, turn.datasets);
};

// System line for R10. Names come from the datasets this viewer has loaded;
// if any id can't be named, say so without naming any.
export const datasetsChangedLine = (ids, nameById) => {
  const names = (ids || []).map((id) => nameById.get(id));
  return names.length > 0 && names.every(Boolean)
    ? `The owner changed the datasets to ${names.join(', ')}.`
    : 'The owner changed the datasets.';
};
